import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Organization, Role } from '@gigdriver/domain';
import { OWNER_ADMIN, MANAGER_PLUS, COORDINATOR_PLUS, hasRoleIn } from '@gigdriver/domain';
import { getOrganization, createOrganization, getUserOrganizations, getOrgMember, updateOrganization } from '@gigdriver/client';
import { useAuthStore } from './auth';

export const useOrganizationStore = defineStore('organization', () => {
  const currentOrg = ref<Organization | null>(null);
  const organizations = ref<Organization[]>([]);
  const myRole = ref<Role | null>(null);
  // The uid myRole/currentOrg were last loaded for. Signing out and back in
  // as a different user (without a full page reload) leaves the Pinia store
  // instance alive — without this, a router guard that skips reloading
  // because `currentOrg.id` already matches the target org would silently
  // keep serving the PREVIOUS user's role to the new session.
  const loadedForUid = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  // The signed-in user's role in each org they belong to (orgId -> role),
  // loaded alongside `organizations`.
  const rolesByOrg = ref<Record<string, Role>>({});
  const membershipsLoaded = ref(false);
  // A user who is `staff` in every org they belong to only needs My Offers /
  // Time Clock — no dashboard, gigs, or org switching.
  const isStaffOnly = computed(
    () => organizations.value.length > 0 && organizations.value.every(o => rolesByOrg.value[o.id] === 'staff'),
  );

  const orgId = computed(() => currentOrg.value?.id || null);
  const isStaff = computed(() => myRole.value === 'staff');
  // Role tiers (see packages/domain/src/access.ts) — UI gating only; the
  // Firestore rules and callables are the real enforcement.
  const isOwnerOrAdmin = computed(() => hasRoleIn(myRole.value, OWNER_ADMIN));
  const isManagerPlus = computed(() => hasRoleIn(myRole.value, MANAGER_PLUS));
  const isCoordinatorPlus = computed(() => hasRoleIn(myRole.value, COORDINATOR_PLUS));

  async function loadMyRole(orgId: string) {
    const uid = useAuthStore().currentUser?.uid;
    loadedForUid.value = uid ?? null;
    if (!uid) {
      myRole.value = null;
      return;
    }
    try {
      const member = await getOrgMember(orgId, uid);
      myRole.value = member?.role ?? null;
    } catch {
      myRole.value = null;
    }
  }

  async function loadUserOrganizations(uid: string) {
    try {
      isLoading.value = true;
      error.value = null;
      const orgs = await getUserOrganizations(uid);
      const roles = await Promise.all(orgs.map(async o => [o.id, (await getOrgMember(o.id, uid))?.role] as const));
      rolesByOrg.value = Object.fromEntries(roles.filter((r): r is [string, Role] => !!r[1]));
      organizations.value = orgs;
      membershipsLoaded.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load organizations';
    } finally {
      isLoading.value = false;
    }
  }

  async function selectOrganization(orgId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      const org = await getOrganization(orgId);
      if (org) {
        currentOrg.value = org;
        await loadMyRole(orgId);
      } else {
        error.value = 'Organization not found';
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load organization';
    } finally {
      isLoading.value = false;
    }
  }

  async function createNewOrganization(name: string, ownerId: string) {
    try {
      isLoading.value = true;
      error.value = null;
      const newOrgId = await createOrganization(name, Intl.DateTimeFormat().resolvedOptions().timeZone);
      const org = await getOrganization(newOrgId);
      if (org) {
        currentOrg.value = org;
        organizations.value.push(org);
        rolesByOrg.value = { ...rolesByOrg.value, [newOrgId]: 'owner' };
        myRole.value = 'owner';
        loadedForUid.value = ownerId;
      }
      return newOrgId;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create organization';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateOrgSettings(orgId: string, updates: Partial<Organization>) {
    try {
      error.value = null;
      await updateOrganization(orgId, updates);
      const org = await getOrganization(orgId);
      if (org && currentOrg.value?.id === orgId) {
        currentOrg.value = org;
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to save settings';
      throw err;
    }
  }

  function clearOrganization() {
    organizations.value = [];
    rolesByOrg.value = {};
    membershipsLoaded.value = false;
    currentOrg.value = null;
    myRole.value = null;
    loadedForUid.value = null;
  }

  return {
    currentOrg,
    organizations,
    orgId,
    myRole,
    loadedForUid,
    isStaff,
    isStaffOnly,
    rolesByOrg,
    membershipsLoaded,
    isOwnerOrAdmin,
    isManagerPlus,
    isCoordinatorPlus,
    isLoading,
    error,
    loadUserOrganizations,
    selectOrganization,
    createNewOrganization,
    updateOrgSettings,
    clearOrganization,
    loadMyRole,
  };
});
